import { FC, useEffect } from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "@/hooks/useAppSelector";
import { createPortal } from "react-dom";
import theme from "@/theme";

interface CustomToastProps {
  message: string;
  success?: boolean;
  onClose: () => void;
  durationMs?: number;
  link?: { text: string; href: string };
}

const CustomToast: FC<CustomToastProps> = ({
  message,
  success,
  onClose,
  durationMs = 5000,
  link,
}) => {
  const location = useLocation();
  const { user: authUser } = useAppSelector((state) => state.auth);
  const myId = authUser?.uid;
  const navigate = useNavigate();
  const pathParts = location.pathname.split("/");
  const currentProfileId = pathParts[2];

  const myProfile = currentProfileId === myId;

  useEffect(() => {
    const timer = setTimeout(onClose, durationMs);
    return () => clearTimeout(timer);
  }, [onClose, message, durationMs]);

  let bgColor = "rgba(42, 133, 255, 0.2)";
  let textColor = theme.palette.custom.mainBlue;
  let linkColor = "#1D6DEB";

  if (success === false) {
    bgColor = "#CE212B33";
    textColor = "#CE212B";
    linkColor = "#991B1B";
  } else if (success === undefined) {
    bgColor = "#2D2D2D";
    textColor = "#FFFFFF";
    linkColor = "#FFFFFF";
  }

  const currentPath = location.pathname;
  const currentSearch = location.search;

  const linkHref = link?.href ?? "";

  const isPostLink = linkHref.startsWith("/post/");

  const isDraftsLink = linkHref.startsWith("/drafts");

  const [linkPath, linkSearch = ""] = linkHref.split("?");

  const isSameDraftsRoute =
    currentPath === "/drafts" &&
    linkPath === "/drafts" &&
    currentSearch.replace("?", "") === linkSearch;

  const hasLink =
    !!link &&
    ((isPostLink && !myProfile) || (!isPostLink && (!isDraftsLink || !isSameDraftsRoute)));

  return createPortal(
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        position: "fixed",
        top: "80px",
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: bgColor,
        color: textColor,
        padding: "12px 24px",
        borderRadius: "12px",
        backdropFilter: "blur(6px)",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
        zIndex: 9999,
        fontFamily: "Inter, sans-serif",
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "14px",
        lineHeight: "20px",
        textAlign: "center",
        minWidth: "472px",
        minHeight: "48px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxWidth: "90vw",
      }}
    >
      <span>
        {message}
        {hasLink && (
          <>
            {" "}
            <Box
              onClick={() => {
                onClose();
                navigate(link.href);
              }}
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontStyle: "normal",
                fontSize: "14px",
                lineHeight: "20px",
                textDecoration: "underline",
                textDecorationStyle: "solid",
                color: linkColor,
                cursor: "pointer",
                display: "inline",
              }}
            >
              {link.text}
            </Box>
          </>
        )}
      </span>
    </Box>,
    document.body
  );
};

export default CustomToast;
